import React, { useState, useEffect } from "react";
import Portal from "../../common/components/Portal";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../project/actions";
import Draggable from "react-draggable";
import ConfirmationModal from "../../common/components/ConfirmationModal";
import { showAlert } from "../../../config/utils";
import * as Service from "../services";

export const BulkQaQc = ({ recommendationIds, projectId, onCompleted }) => {
    const [showModal, setShowModal] = useState(false);
    const [isQaQcReviewerUser, setIsQaQcReviewerUser] = useState(false);

    const dispatch = useDispatch();
    const reviewers = useSelector(state => state.projectReducer.getReviewersByProjectData?.reviewer);

    useEffect(() => {
        const checkQaQcReviewer = async () => {
            let isReviewer = localStorage.getItem("role") === "super_admin";
            if (!isReviewer && projectId) {
                await dispatch(
                    actions.getReviewersByProject({
                        project_id: projectId || "",
                        user_id: localStorage.getItem("userId") || "",
                        is_active: true
                    })
                );
            }
        };
        checkQaQcReviewer();
        // eslint-disable-next-line
    }, [projectId, dispatch]);

    useEffect(() => {
        let isReviewer = localStorage.getItem("role") === "super_admin";
        if (!isReviewer && Array.isArray(reviewers) && reviewers.length > 0) {
            isReviewer = true;
        }
        setIsQaQcReviewerUser(isReviewer);
    }, [reviewers]);

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    if (!isQaQcReviewerUser) return null;

    return (
        <>
            <button className="add-btn ml-2 btn-new-noti" onClick={handleOpenModal}>
                <i className="fas fa-check-double mr-1"></i>
                {`Bulk QA/QC`}
            </button>
            {showModal ? (
                <Portal
                    body={
                        <BulkQaQcCreate
                            recommendationIds={recommendationIds}
                            onCancel={handleCloseModal}
                            onCompleted={onCompleted}
                            title={`Bulk QA/QC (${recommendationIds?.length || 0})`}
                        />
                    }
                    onCancel={handleCloseModal}
                />
            ) : null}
        </>
    );
};

const BulkQaQcCreate = ({ recommendationIds, onCancel, onCompleted, title }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [issueTitle, setIssueTitle] = useState("");
    const [isErr, setIsErr] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const handleChange = e => {
        setIssueTitle(e.target.value);
        setIsErr(false);
    };

    const handleSave = async () => {
        if (!issueTitle?.trim()) {
            setIsErr(true);
            return;
        }
        setShowConfirmation(true);
    };

    const handleConfirmSave = async () => {
        setIsLoading(true);
        setShowConfirmation(false);
        try {
            const response = await Service.createQaQcIssue({
                recommendation_ids: recommendationIds,
                recommendation_review: { title: issueTitle }
            });

            setIsLoading(false);

            const data = response?.data;

            if (data?.success === false) {
                showAlert(data?.message || "An error occurred while creating QA/QC issue.");
                return;
            }

            if (response?.status === 200) {
                showAlert("Bulk QA/QC Created Successfully");
                onCompleted && onCompleted();
                onCancel && onCancel();
            } else {
                const errorMsg =
                    response?.message ||
                    (response?.errors && typeof response.errors === "object" && Object.values(response.errors).join(", ")) ||
                    "An error occurred while creating QA/QC issue. Please try again.";
                showAlert(errorMsg);
            }
        } catch (error) {
            setIsLoading(false);
            const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || "An unexpected error occurred!";
            showAlert(message);
        }
    };

    const handleCancelConfirmation = () => {
        setShowConfirmation(false);
    };

    return (
        <>
            {showConfirmation && (
                <Portal
                    body={
                        <ConfirmationModal
                            heading={`You are about to add QA/QC to ${recommendationIds?.length || 0} Recommendation(s).`}
                            message={"Are you sure you want to continue?"}
                            type="cancel"
                            onNo={handleCancelConfirmation}
                            onYes={handleConfirmSave}
                        />
                    }
                    onCancel={handleCancelConfirmation}
                />
            )}
            <div className="modal modal-region qa-band-modl" tabIndex="-1" style={{ display: "block" }} role="dialog">
                <Draggable handle=".draggable" cancel=".close">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header draggable">
                                <h5 className="modal-title">
                                    <div className="txt-hed">{title || `Bulk QA/QC (${recommendationIds?.length || 0})`}</div>
                                </h5>
                                <button type="button" className="close close-icn-sec" onClick={onCancel}>
                                    <span aria-hidden="true">
                                        <img src="/img/close.svg" alt="" />
                                    </span>
                                </button>
                            </div>
                            <div className="modal-body region-otr">
                                <div className="col-md-12 more-info-content p-0">
                                    <div className="form-group">
                                        <label>Issue title</label>
                                        <textarea
                                            placeholder="Notes"
                                            autoFocus
                                            className={`form-control ${isErr ? "error-border" : ""}`}
                                            value={issueTitle}
                                            onChange={handleChange}
                                        />
                                        {isErr && <div className="text-danger mt-1">Title is required.</div>}
                                    </div>
                                </div>
                            </div>
                            <div
                                className="mb-2 w-100"
                                style={{
                                    fontSize: "0.93em",
                                    color: "#b94a48",
                                    background: "#fff3cd",
                                    border: "1px solid #ffeeba",
                                    borderRadius: "4px",
                                    padding: "10px 12px"
                                }}
                            >
                                <i className="fas fa-exclamation-triangle mr-2" style={{ color: "#b94a48" }}></i>
                                <span>
                                    <strong>Note:</strong> Once the QA/QC is created for the selected recommendations, you will only be able to edit
                                    or delete each QA/QC from the individual recommendation's detail page. Bulk edits or deletes are not available
                                    here.
                                </span>
                            </div>
                            <div className="modal-footer">
                                <div className="btn-sec">
                                    <div className="text-right btnOtr edit-cmn-btn">
                                        <button
                                            type="button"
                                            className="btn btn-secondary btnClr col-md-2 mr-1"
                                            data-dismiss="modal"
                                            onClick={onCancel}
                                        >
                                            Cancel
                                        </button>
                                        <button disabled={isLoading} type="button" className="btn btn-primary btnRgion col-md-2" onClick={handleSave}>
                                            {isLoading ? (
                                                <div className="button-loader d-flex justify-content-center align-items-center">
                                                    <div className="spinner-border text-white" role="status">
                                                        <span className="sr-only">Loading...</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                "Save"
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Draggable>
            </div>
        </>
    );
};
